import { Injectable } from "@angular/core";
import { BehaviorSubject, combineLatest, map } from "rxjs";
import { ApiService } from "./api.service";

@Injectable({ providedIn: "root" })
export class LocationStatisticsService {
	constructor(private apiService: ApiService) {}

	private readonly serverStats$ = new BehaviorSubject<LocationStatisticsResult | null>(null);
	private readonly holdDelta$ = new BehaviorSubject<{ riders: number; dispatches: number } | null>(null);

	readonly statistics$ = combineLatest([this.serverStats$, this.holdDelta$]).pipe(
		map(([stats, delta]) => {
			if (stats == null || delta == null) {
				return stats;
			}
			return {
				...stats,
				lastHour: {
					...stats.lastHour,
					riders: (stats.lastHour?.riders ?? 0) + delta.riders,
					dispatches: (stats.lastHour?.dispatches ?? 0) + delta.dispatches,
				},
			};
		}),
	);

	refresh = async (): Promise<LocationStatisticsResult | null> => {
		try {
			const stats = await this.apiService.getStatistics(false);
			this.serverStats$.next(stats ?? null);
			this.holdDelta$.next(null);
			return stats ?? null;
		} catch {
			return this.serverStats$.getValue();
		}
	};

	beginHoldOptimistic = (riders: number, dispatches = 1) => {
		this.holdDelta$.next({ riders, dispatches });
	};

	clearHoldOptimistic = () => {
		this.holdDelta$.next(null);
	};

	foldHoldOptimisticIntoServer = () => {
		const stats = this.serverStats$.getValue();
		const delta = this.holdDelta$.getValue();
		if (stats == null || delta == null) {
			this.holdDelta$.next(null);
			return;
		}
		this.serverStats$.next({
			...stats,
			lastHour: {
				...stats.lastHour,
				riders: (stats.lastHour?.riders ?? 0) + delta.riders,
				dispatches: (stats.lastHour?.dispatches ?? 0) + delta.dispatches,
			},
		});
		this.holdDelta$.next(null);
	};
}
